/**
 * CreateOrderFromEmailButton - Shown in the email modal to let users manually
 * create an order draft linked to this email.
 *
 * Two modes:
 *   1. No order exists: "Create Order Draft" — primary CTA
 *   2. Order already exists: "Add Another Order" — secondary, for multi-order emails
 *
 * Uses POST /portal/orders/from-email/{emailId}, ensuring the order
 * is linked via source_email_id.
 *
 * Recording (kanban 1136a376): the click is the create_order_from_email
 * row (data-action, email id); the created draft is an order_created row
 * carrying BOTH the source email id and the new order id, the same name the
 * Orders page records for a manual draft, so a trace keyed on the order
 * reaches back to the purchase-order email it was made from.
 */

import { useState } from 'react';
import { portalOrderService } from '../../services/portalOrders';
import { trackEvent } from '../../services/portalTracking';
import styles from './CreateOrderFromEmailButton.module.css';

interface CreateOrderFromEmailButtonProps {
  emailId: string;
  /** Called after the order draft is successfully created, with the new order's ID. */
  onOrderCreated: (emailId: string, orderId: string) => void;
  /** When true, renders as a compact "Add Another Order" variant. */
  hasExistingOrder?: boolean;
}

const CreateOrderFromEmailButton: React.FC<CreateOrderFromEmailButtonProps> = ({
  emailId,
  onOrderCreated,
  hasExistingOrder = false,
}) => {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const result = await portalOrderService.createOrderFromEmail(emailId);
      trackEvent('action', 'order_created', {
        order_id: result.order.id,
        email_id: emailId,
        source: 'email',
      });
      onOrderCreated(emailId, result.order.id);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { detail?: string } } }).response
              ?.data?.detail ?? 'Failed to create order')
          : 'Failed to create order';
      setError(message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className={hasExistingOrder ? styles.containerCompact : styles.container}>
      <button
        className={hasExistingOrder ? styles.buttonCompact : styles.button}
        onClick={handleCreate}
        disabled={creating}
        type="button"
        data-action="create_order_from_email"
        data-target-email-id={emailId}
        data-outcome="request"
      >
        <svg
          className={styles.icon}
          width={hasExistingOrder ? 14 : 18}
          height={hasExistingOrder ? 14 : 18}
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-6-2c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2z" />
        </svg>
        <div className={styles.content}>
          <div className={hasExistingOrder ? styles.titleCompact : styles.title}>
            {creating
              ? 'Creating Order...'
              : hasExistingOrder
                ? 'Add Another Order'
                : 'Create Order Draft'}
          </div>
          {!hasExistingOrder && (
            <div className={styles.subtitle}>
              {creating ? 'Please wait' : 'Link a new order to this email'}
            </div>
          )}
        </div>
        <svg
          className={styles.plusIcon}
          width={hasExistingOrder ? 14 : 16}
          height={hasExistingOrder ? 14 : 16}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="16" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
      </button>
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

export default CreateOrderFromEmailButton;
